/**
 * Styles for the FieldNotes component.
 */

import { StyleSheet } from 'react-native';
import { color, space, text, radius } from '../../../shared/tokens/mobile';

export const styles = StyleSheet.create({
  section: {
    marginHorizontal: space.md,
    marginBottom: space.lg,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    marginBottom: space.sm,
  },
  sectionTitle: {
    fontSize: text.bodyMedium.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
  },
  badge: {
    fontSize: 11,
    fontWeight: '600',
    color: color.surface,
    backgroundColor: color.primary,
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 10,
    overflow: 'hidden',
    minWidth: 20,
    textAlign: 'center',
  },
  addBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: color.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },

  emptyState: {
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.md,
    padding: space.lg,
    alignItems: 'center',
    gap: space.sm,
    borderWidth: 1,
    borderColor: color.divider,
    borderStyle: 'dashed',
  },
  emptyText: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    textAlign: 'center',
    lineHeight: 18,
  },

  noteCard: {
    backgroundColor: color.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.sm,
    borderWidth: 1,
    borderColor: color.divider,
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    marginBottom: space.xs,
  },
  categoryBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  categoryText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  noteTime: {
    flex: 1,
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    textAlign: 'right',
    marginRight: space.xs,
  },
  noteTitle: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    marginBottom: 2,
  },
  noteBody: {
    fontSize: text.body.fontSize,
    color: color.textSecondary,
    lineHeight: 20,
  },
  noteMeta: {
    flexDirection: 'row',
    gap: space.md,
    marginTop: space.sm,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  metaText: {
    fontSize: 11,
    color: color.textMuted,
  },

  toggleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: space.xs,
    paddingVertical: space.sm,
  },
  toggleText: {
    fontSize: text.caption.fontSize,
    color: color.primary,
    fontWeight: '600',
  },
  errorText: {
    fontSize: text.caption.fontSize,
    color: color.error,
    textAlign: 'center',
    marginTop: space.xs,
  },

  composerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  composerCard: {
    backgroundColor: color.surface,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    padding: space.lg,
    paddingBottom: space.xxl,
    maxHeight: '80%',
  },
  composerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: space.md,
  },
  composerTitle: {
    fontSize: text.sectionTitle.fontSize,
    fontWeight: text.sectionTitle.fontWeight,
    color: color.textPrimary,
  },

  categoryRow: {
    marginBottom: space.md,
    flexGrow: 0,
  },
  categoryChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: color.divider,
    marginRight: space.xs,
  },
  chipText: {
    fontSize: 12,
    color: color.textMuted,
    textTransform: 'capitalize',
  },

  titleInput: {
    fontSize: text.body.fontSize,
    color: color.textPrimary,
    borderWidth: 1,
    borderColor: color.divider,
    borderRadius: radius.md,
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    marginBottom: space.sm,
  },
  bodyInput: {
    fontSize: text.body.fontSize,
    color: color.textPrimary,
    borderWidth: 1,
    borderColor: color.divider,
    borderRadius: radius.md,
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    minHeight: 120,
    marginBottom: space.md,
  },

  composerFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  geoHint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flex: 1,
  },
  geoHintText: {
    fontSize: 11,
    color: color.textMuted,
  },
  saveBtn: {
    backgroundColor: color.primary,
    paddingHorizontal: space.lg,
    paddingVertical: space.sm + 2,
    borderRadius: radius.md,
    minWidth: 100,
    alignItems: 'center',
  },
  saveBtnDisabled: {
    opacity: 0.4,
  },
  saveBtnText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.surface,
  },

  confirmOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: space.xl,
  },
  confirmCard: {
    backgroundColor: color.surface,
    borderRadius: radius.lg,
    padding: space.xl,
    width: '100%',
    maxWidth: 340,
  },
  confirmTitle: {
    fontSize: text.sectionTitle.fontSize,
    fontWeight: text.sectionTitle.fontWeight,
    color: color.textPrimary,
    textAlign: 'center',
    marginBottom: space.xs,
  },
  confirmMessage: {
    fontSize: text.body.fontSize,
    color: color.textSecondary,
    textAlign: 'center',
    marginBottom: space.lg,
  },
  confirmActions: {
    flexDirection: 'row',
    gap: space.sm,
  },
  confirmBtn: {
    flex: 1,
    paddingVertical: space.sm + 2,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmCancelBtn: {
    backgroundColor: color.backgroundSecondary,
  },
  confirmCancelText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textSecondary,
  },
  confirmDeleteBtn: {
    backgroundColor: color.error,
  },
  confirmDeleteText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.surface,
  },
});
